import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { EmbeddedJobDetailsPanel } from 'components/Pages/Jobs/JobDrawer';
import { useImageAnalysisStatus } from 'components/StudyDashboard/Workflow/useImageAnalysisStatus';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { Permission } from 'interfaces/permissionOption';
import React, { useId, useState } from 'react';
import { humanize } from 'utils/helpers';
import { usePermissions } from 'utils/usePermissions';
import type { RestoredCellTypingExecution } from './previousExecution';

export const PanelInputJobs = ({
  studyId,
  labId,
  panel,
  selectedSlideIds,
  manualSource,
}: {
  studyId?: string;
  labId?: string;
  panel: MultiplexPanelView;
  selectedSlideIds: string[];
  manualSource?: RestoredCellTypingExecution;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [viewedJobId, setViewedJobId] = useState<string | null>(null);
  const jobTitleId = useId();
  const { hasPermission } = usePermissions();
  const canViewJobs = hasPermission(Permission.ViewJobs);
  const selectedIds = new Set(selectedSlideIds);
  const slides = panel.slides.filter((slide) => selectedIds.has(slide.slideId));
  const currentPage = Math.min(page, Math.max(0, Math.ceil(slides.length / 10) - 1));
  const visibleSlides = slides.slice(currentPage * 10, currentPage * 10 + 10);
  const jobs = useImageAnalysisStatus({
    studyId: studyId ?? '',
    labId: labId ?? '',
    slideIds: visibleSlides.map((slide) => slide.slideId),
    enabled: expanded && canViewJobs && Boolean(studyId && labId) && visibleSlides.length > 0,
  });
  return (
    <>
      <Accordion disableGutters expanded={expanded} onChange={(_event, value) => setExpanded(value)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <HistoryOutlinedIcon color="action" />
            <Typography variant="h3" component="h3">
              Advanced: input jobs
            </Typography>
          </Stack>
        </AccordionSummary>
        {expanded && (
          <AccordionDetails>
            {!canViewJobs ? (
              <Alert severity="info">Job-view permission is required.</Alert>
            ) : !studyId || !labId ? (
              <Alert severity="info">Study and lab scope are required.</Alert>
            ) : !slides.length ? (
              <Alert severity="info">Select at least one panel slide.</Alert>
            ) : (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Execution resolves latest artifacts; these attempts are not frozen input selections.
                </Typography>
                {manualSource && (
                  <Typography variant="body2">
                    Stored inputs from{' '}
                    <Button
                      color="inherit"
                      variant="text"
                      size="small"
                      startIcon={<InfoOutlinedIcon />}
                      onClick={() => setViewedJobId(manualSource.sourceJobId)}
                    >
                      {manualSource.label}
                    </Button>
                  </Typography>
                )}
                {jobs.isLoading ? (
                  <Skeleton height={120} aria-label="Loading upstream jobs" />
                ) : jobs.isError ? (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<RefreshOutlinedIcon />}
                        onClick={() => void jobs.refetch()}
                      >
                        Retry
                      </Button>
                    }
                  >
                    Upstream jobs could not be loaded.
                  </Alert>
                ) : (
                  <>
                    <TableContainer>
                      <Table size="small" aria-label={`${panel.name || 'Panel'} upstream jobs`}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Slide</TableCell>
                            <TableCell>Latest job attempts</TableCell>
                            {manualSource && <TableCell>Stored inputs</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleSlides.map((slide) => {
                            const attempts =
                              jobs.data?.statuses.filter((entry) => entry.slideId === slide.slideId) ?? [];
                            const storedInputs = manualSource
                              ? Object.values(manualSource.frozenParams.inputs).flatMap(
                                  (caseInputs) => caseInputs[slide.slideId] ?? []
                                )
                              : [];
                            return (
                              <TableRow key={slide.slideId}>
                                <TableCell sx={{ overflowWrap: 'anywhere' }}>
                                  {slide.originalFileName || slide.slideId}
                                  <Typography variant="caption" display="block">
                                    {slide.slideId}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {attempts.length ? (
                                    <Stack spacing={1}>
                                      {attempts.map((entry) => (
                                        <Stack
                                          key={`${entry.jobType}-${entry.jobId}`}
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          flexWrap="wrap"
                                          useFlexGap
                                        >
                                          <Typography variant="body2">{humanize(entry.jobType)}</Typography>
                                          <Button
                                            color="inherit"
                                            variant="text"
                                            size="small"
                                            startIcon={<InfoOutlinedIcon />}
                                            aria-label={`View upstream job ${entry.jobId}`}
                                            onClick={() => setViewedJobId(entry.jobId)}
                                          >
                                            {entry.jobId}
                                          </Button>
                                          <Chip size="small" label={humanize(entry.status)} />
                                          <Chip size="small" variant="outlined" label="Latest attempt" />
                                        </Stack>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No upstream job returned.
                                    </Typography>
                                  )}
                                </TableCell>
                                {manualSource && (
                                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                                    {storedInputs.length
                                      ? storedInputs.map((entry, index) => {
                                          const reference = { ...entry.input, ...entry.override }.artifactUrl;
                                          return (
                                            <Typography key={index} variant="body2" component="div">
                                              {reference?.startsWith('artifact://')
                                                ? reference
                                                : reference
                                                ? 'Stored artifact reference'
                                                : 'Missing artifact reference'}
                                            </Typography>
                                          );
                                        })
                                      : 'No stored inputs for this slide.'}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={slides.length}
                      page={currentPage}
                      rowsPerPage={10}
                      rowsPerPageOptions={[10]}
                      onPageChange={(_event, nextPage) => setPage(nextPage)}
                    />
                  </>
                )}
              </Stack>
            )}
          </AccordionDetails>
        )}
      </Accordion>
      {canViewJobs && viewedJobId && (
        <Dialog open fullWidth maxWidth="sm" aria-labelledby={jobTitleId} onClose={() => setViewedJobId(null)}>
          <DialogChromeHeader
            titleId={jobTitleId}
            title="Input job details"
            closeButtonAriaLabel="Close input job details"
            onClose={() => setViewedJobId(null)}
          />
          <DialogContent dividers>
            <EmbeddedJobDetailsPanel currentJobId={viewedJobId} />
          </DialogContent>
          <DialogChromeFooter>
            <Button color="inherit" onClick={() => setViewedJobId(null)}>
              Close
            </Button>
          </DialogChromeFooter>
        </Dialog>
      )}
    </>
  );
};
